// services/v2/jobs/jobApi.ts
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { sec } from "../../../security";
import { addNotification } from "../../notifications/notificationsSlice"; // <- toast
import { Job } from "./model/job";
import { JobFolder } from "./model/jobFolder";

const apiServerUrl = process.env.REACT_APP_HAISTACK_API_URL;

export const jobApi = createApi({
  reducerPath: "jobApiV2",
  tagTypes: ["Jobs", "Job", "JobFolders"],
  baseQuery: fetchBaseQuery({
    baseUrl: apiServerUrl,
    prepareHeaders: async (headers) => {
      const token = await sec.getAccessTokenSilently()();
      if (token) headers.set("authorization", `Bearer ${token}`);
      return headers;
    }
  }),
  endpoints: (builder) => ({
    getJobs: builder.query<Job[], void>({
      query: () => "api/v2/jobs",
      transformResponse: (response: { jobs: Job[] }) => response.jobs,
      providesTags: ["Jobs"]
    }),
    getJob: builder.query<Job, string>({
      query: (id) => `api/v2/jobs/${id}`,
      transformResponse: (response: { job: Job }) => response.job,
      providesTags: (result, error, id) => [{ type: "Job", id }]
    }),
    getJobFolders: builder.query<JobFolder[], void>({
      query: () => "api/v2/job_folders",
      transformResponse: (response: { job_folders: JobFolder[] }) =>
        response.job_folders,
      providesTags: ["JobFolders"]
    }),
    postJobFolder: builder.mutation<JobFolder, { name: string }>({
      query: ({ name }) => ({
        url: "api/v2/job_folders",
        method: "POST",
        body: {
          job_folder: {
            name
          }
        }
      }),
      invalidatesTags: ["JobFolders"],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            addNotification({
              message: "Job folder created successfully",
              type: "success",
              placement: "bottomRight"
            })
          );
        } catch (_e) {
          dispatch(
            addNotification({
              message: "Failed to create job folder",
              type: "danger",
              placement: "bottomRight"
            })
          );
        }
      }
    }),
    patchJobFolder: builder.mutation<JobFolder, { id: string; name: string }>({
      query: ({ id, name }) => ({
        url: `api/v2/job_folders/${id}`,
        method: "PUT",
        body: {
          job_folder: {
            name
          }
        }
      }),
      invalidatesTags: ["JobFolders", "Jobs"],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            addNotification({
              message: "Job folder updated successfully",
              type: "success",
              placement: "bottomRight"
            })
          );
        } catch (_e) {
          dispatch(
            addNotification({
              message: "Failed to update job folder",
              type: "danger",
              placement: "bottomRight"
            })
          );
        }
      }
    }),
    deleteJobFolder: builder.mutation<any, { id: string }>({
      query: ({ id }) => ({
        url: `api/v2/job_folders/${id}`,
        method: "DELETE"
      }),
      invalidatesTags: ["JobFolders", "Jobs"],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            addNotification({
              message: "Job folder deleted successfully",
              type: "success",
              placement: "bottomRight"
            })
          );
        } catch (_e) {
          dispatch(
            addNotification({
              message: "Failed to delete job folder",
              type: "danger",
              placement: "bottomRight"
            })
          );
        }
      }
    }),
    moveJobToFolder: builder.mutation<
      any,
      { jobId: string; folderId: string | null }
    >({
      query: ({ jobId, folderId }) => {
        if (folderId === null) {
          // Remove job from folder
          return {
            url: `api/v2/job_folder_assignments/${jobId}`,
            method: "DELETE"
          };
        } else {
          // Assign job to folder
          return {
            url: "api/v2/job_folder_assignments",
            method: "POST",
            body: {
              job_id: jobId,
              job_folder_id: folderId
            }
          };
        }
      },
      invalidatesTags: ["Jobs", "JobFolders"],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          const action = _arg.folderId === null ? "removed from" : "moved to";
          dispatch(
            addNotification({
              message: `Job ${action} folder successfully`,
              type: "success",
              placement: "bottomRight"
            })
          );
        } catch (_e) {
          dispatch(
            addNotification({
              message: "Failed to move job to folder",
              type: "danger",
              placement: "bottomRight"
            })
          );
        }
      }
    }),
    bulkAssignJobsToFolder: builder.mutation<
      any,
      { jobIds: string[]; folderId: string }
    >({
      query: ({ jobIds, folderId }) => ({
        url: "api/v2/job_folder_assignments/bulk_assign",
        method: "POST",
        body: {
          job_ids: jobIds,
          job_folder_id: folderId
        }
      }),
      invalidatesTags: ["Jobs", "JobFolders"],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            addNotification({
              message: `Successfully assigned ${_arg.jobIds.length} jobs to folder`,
              type: "success",
              placement: "bottomRight"
            })
          );
        } catch (_e) {
          dispatch(
            addNotification({
              message: "Failed to assign jobs to folder",
              type: "danger",
              placement: "bottomRight"
            })
          );
        }
      }
    }),
    bulkRemoveJobsFromFolders: builder.mutation<any, { jobIds: string[] }>({
      query: ({ jobIds }) => ({
        url: "api/v2/job_folder_assignments/bulk_remove",
        method: "POST",
        body: {
          job_ids: jobIds
        }
      }),
      invalidatesTags: ["Jobs", "JobFolders"],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            addNotification({
              message: `Successfully removed ${_arg.jobIds.length} jobs from folders`,
              type: "success",
              placement: "bottomRight"
            })
          );
        } catch (_e) {
          dispatch(
            addNotification({
              message: "Failed to remove jobs from folders",
              type: "danger",
              placement: "bottomRight"
            })
          );
        }
      }
    }),
    getJobMarketIntel: builder.query<any, { jobId: string }>({
      query: ({ jobId }) => `api/v2/jobs/${jobId}/market_intel`,
      transformResponse: (response: { market_intel: any }) =>
        response.market_intel
    }),
    getJobNotes: builder.query<any, { jobId: string }>({
      query: ({ jobId }) => `api/v2/jobs/${jobId}/notes`,
      transformResponse: (response: { notes: any }) => response.notes
    }),
    postJobNote: builder.mutation<any, { jobId: string; note: string }>({
      query: ({ jobId, note }) => ({
        url: `api/v2/jobs/${jobId}/notes`,
        method: "POST",
        body: { note }
      }),
      invalidatesTags: (result, error, { jobId }) => [
        { type: "Job", id: jobId }
      ]
    }),
    patchJobNote: builder.mutation<any, { jobId: string; note: string }>({
      query: ({ jobId, note }) => ({
        url: `api/v2/jobs/${jobId}/notes`,
        method: "PATCH",
        body: { note }
      }),
      invalidatesTags: (result, error, { jobId }) => [
        { type: "Job", id: jobId }
      ]
    }),
    deleteJobNote: builder.mutation<any, { jobId: string; noteId: string }>({
      query: ({ jobId, noteId }) => ({
        url: `api/v2/jobs/${jobId}/notes/${noteId}`,
        method: "DELETE"
      }),
      invalidatesTags: (result, error, { jobId }) => [
        { type: "Job", id: jobId }
      ]
    }),
    patchJob: builder.mutation<any, { jobId: string; job: any }>({
      query: ({ jobId, job }) => ({
        url: `api/v2/jobs/${jobId}`,
        method: "PATCH",
        body: { job }
      }),
      invalidatesTags: (result, error, { jobId }) => [
        { type: "Job", id: jobId },
        "Jobs"
      ],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            addNotification({
              message: "Job updated successfully",
              type: "success",
              placement: "bottomRight"
            })
          );
        } catch (_e) {
          dispatch(
            addNotification({
              message: "Failed to update job",
              type: "danger",
              placement: "bottomRight"
            })
          );
        }
      }
    }),
    postNewJob: builder.mutation<any, any>({
      query: (job) => ({
        url: "api/v2/jobs",
        method: "POST",
        body: { job }
      }),
      invalidatesTags: ["Jobs"],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            addNotification({
              message: "Job created successfully",
              type: "success",
              placement: "bottomRight"
            })
          );
        } catch (_e) {
          dispatch(
            addNotification({
              message: "Failed to create job",
              type: "danger",
              placement: "bottomRight"
            })
          );
        }
      }
    }),
    postFetchPostingData: builder.mutation<
      any,
      { headline: string; description: string }
    >({
      query: ({ headline, description }) => ({
        url: "api/v2/job_posting_data",
        method: "POST",
        body: {
          headline,
          description
        }
      })
    }),
    shareTargetList: builder.mutation({
      query: ({ jobId, emails }) => ({
        url: `/api/v2/jobs/${jobId}/shared_target_lists`,
        method: "POST",
        body: {
          emails
        }
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            addNotification({
              message: "Shared candidates list successfully",
              type: "success"
            })
          );
        } catch {
          dispatch(
            addNotification({
              message: "Failed to share candidates list",
              type: "danger"
            })
          );
        }
      }
    }),
    getSharedTargetListResponse: builder.query({
      query: ({ jobId }) => `/api/v2/jobs/${jobId}/shared_target_lists_response`
    }),
    getPartnersToShare: builder.query({
      query: () => `/api/v2/my_employees/partners`
    }),
    pinJob: builder.mutation<any, { jobId: string }>({
      query: ({ jobId }) => ({
        url: `api/v2/jobs/${jobId}/pin`,
        method: "PATCH"
      }),
      invalidatesTags: (result, error, { jobId }) => [
        { type: "Job", id: jobId },
        "Jobs"
      ],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            addNotification({
              message: "Job pinned successfully",
              type: "success",
              placement: "bottomRight"
            })
          );
        } catch (_e) {
          dispatch(
            addNotification({
              message: "Failed to pin job",
              type: "danger",
              placement: "bottomRight"
            })
          );
        }
      }
    }),
    unpinJob: builder.mutation<any, { jobId: string }>({
      query: ({ jobId }) => ({
        url: `api/v2/jobs/${jobId}/unpin`,
        method: "PATCH"
      }),
      invalidatesTags: (result, error, { jobId }) => [
        { type: "Job", id: jobId },
        "Jobs"
      ],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            addNotification({
              message: "Job unpinned successfully",
              type: "success",
              placement: "bottomRight"
            })
          );
        } catch (_e) {
          dispatch(
            addNotification({
              message: "Failed to unpin job",
              type: "danger",
              placement: "bottomRight"
            })
          );
        }
      }
    })
  })
});

export const {
  useGetJobsQuery,
  useGetJobQuery,
  useGetJobFoldersQuery,
  usePostJobFolderMutation,
  usePatchJobFolderMutation,
  useDeleteJobFolderMutation,
  useMoveJobToFolderMutation,
  useBulkAssignJobsToFolderMutation,
  useBulkRemoveJobsFromFoldersMutation,
  useGetJobMarketIntelQuery,
  useGetJobNotesQuery,
  usePostJobNoteMutation,
  usePatchJobNoteMutation,
  useDeleteJobNoteMutation,
  usePatchJobMutation,
  usePostNewJobMutation,
  usePostFetchPostingDataMutation,
  useShareTargetListMutation,
  useGetSharedTargetListResponseQuery,
  useGetPartnersToShareQuery,
  usePinJobMutation,
  useUnpinJobMutation
} = jobApi;
